import { Skeleton } from '../primitives/Skeleton'

/**
 * Loading placeholder for {@link PersonalBlock}. Mirrors the real layout —
 * the `dash-grid` with a Leave Rhythm card (ring + summary lines) on the left
 * and the My Leave balance-tile grid on the right — so the home surface reveals
 * its structure before data lands rather than flashing `…` counters and then
 * popping the rhythm card in late.
 *
 * Shown at the top of every role's home page while the profile / balances /
 * health-bar queries are still pending. Follows the same shape-the-real-thing
 * approach as `RosterSkeleton`.
 */
export function PersonalBlockSkeleton(): JSX.Element {
  return (
    <div
      className="dash-grid"
      data-testid="personal-block-skeleton"
      aria-busy="true"
      aria-label="Loading your leave summary"
      style={{ marginTop: 18, display: 'grid', gap: 16, alignItems: 'stretch' }}
    >
      <RhythmCardSkeleton />
      <BalancesCardSkeleton />
    </div>
  )
}

/** Leave Rhythm card placeholder: eyebrow, ring + summary lines, status box. */
function RhythmCardSkeleton(): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '18px 18px 16px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Eyebrow — "LEAVE RHYTHM" */}
      <Skeleton width={92} height={10} radius={4} style={{ marginBottom: 14 }} />

      {/* Ring (left) + headline/sub lines (right) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <Skeleton width={104} height={104} radius={52} style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 }}>
          <Skeleton width="72%" height={15} radius={4} />
          <Skeleton width="100%" height={11} radius={3} />
          <Skeleton width="86%" height={11} radius={3} />
        </div>
      </div>

      {/* Status box — matches the "no patterns / active patterns" block */}
      <Skeleton height={40} radius={8} />
    </div>
  )
}

/** My Leave card placeholder: eyebrow + the 3-column balance-tile grid. */
function BalancesCardSkeleton(): JSX.Element {
  // Eight tiles mirrors a fully-populated grid (annual + five common types +
  // a gender-gated type + replacement); the exact count only affects the
  // placeholder height, and the real grid slots in over it on load.
  const tiles = Array.from({ length: 8 }, (_, i) => i)
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '14px 18px',
      }}
    >
      {/* Eyebrow — "MY LEAVE · YYYY" */}
      <Skeleton width={112} height={10} radius={4} />

      <div className="balance-tiles" style={{ marginTop: 14, display: 'grid', gap: 10 }}>
        {tiles.map((i) => (
          <BalanceTileSkeleton key={i} />
        ))}
      </div>
    </div>
  )
}

/** One leave-type tile placeholder: label, big counter, progress bar. */
function BalanceTileSkeleton(): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '12px 14px',
        minHeight: 82,
        boxSizing: 'border-box',
      }}
    >
      <Skeleton width="62%" height={9} radius={3} style={{ marginBottom: 10 }} />
      <Skeleton width={40} height={22} radius={4} />
      <Skeleton height={3} radius={99} style={{ marginTop: 10 }} />
      <Skeleton width="45%" height={9} radius={3} style={{ marginTop: 8 }} />
    </div>
  )
}
